/**
 * Processes an array of citation/reference objects to ensure they have a consistent basic structure.
 * This is a simplified version, as AI-generated references should already be well-structured.
 * This can be used for manually added citations or as a light validation step.
 */
export const processCitations = (references = []) => {
  if (!Array.isArray(references)) {
    console.warn('processCitations received non-array input:', references);
    return [];
  }
  return references.filter(ref => ref !== null && ref !== undefined).map((ref, idx) => {
    const baseRef = (typeof ref === 'object' && ref !== null) ? { ...ref } : { title: String(ref) };

    // Ensure basic fields
    const processedRef = {
      // Always use sequential numbering for display ID, preserve original ID as internalId if needed
      id: `[${idx + 1}]`,
      internalId: baseRef.id || baseRef.internalId, // Preserve original unique ID for database/React keys
      title: baseRef.title || 'Untitled Source',
      url: baseRef.url || '',
      type: baseRef.type || 'unknown',
      description: baseRef.description || baseRef.referenceText || '',
      referenceText: baseRef.referenceText || '',
      retrievedInfo: Array.isArray(baseRef.retrievedInfo) ? baseRef.retrievedInfo : [],
      publicationDate: baseRef.publicationDate || null,
      license: baseRef.license || null,
    };

    // Basic URL validation/prefixing if it looks like a URL but lacks a scheme
    if (processedRef.url && !/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//i.test(processedRef.url)) {
      if (/^(?:(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|localhost:\d+)/i.test(processedRef.url)) {
        processedRef.url = `http://${processedRef.url}`;
      } else {
        // If it doesn't look like a domain, it might be a placeholder or internal link, leave as is or clear
        // For now, leave as is if it's not an obvious domain needing a scheme.
      }
    }
    return processedRef;
  });
};

// isBibliographic and genGoogleBooksUrl might still be useful for manual citation input UIs
// but are not directly part of the AI flow anymore. Keeping them for potential utility.
const isBibliographic = txt => {
  if (!txt || typeof txt !== 'string') return false;
  return /\d{4}/.test(txt) && /[A-Z][a-z]+/.test(txt); // Checks for a year and capitalized words
};

const genGoogleBooksUrl = txt => {
  if (!txt || typeof txt !== 'string') return `https://www.google.com/search?tbm=bks&q=Unknown+Title`;
  return `https://www.google.com/search?tbm=bks&q=${encodeURIComponent(txt)}`;
};

/**
 * Renumbers in-text citation markers (e.g., [1], [2]) to match the order and IDs
 * of a provided list of reference objects.
 *
 * @param {string} text The text content containing citation markers.
 * @param {Array<Object>} referencesArray An array of reference objects,
 *                        each expected to have a unique `id` property
 *                        (e.g., "[1]", "[2]", etc.) that should be used for replacement.
 * @returns {string} The text with in-text citation markers renumbered/aligned.
 */
export const renumberInTextCitations = (text, referencesArray = []) => {
  if (typeof text !== 'string' || !text) {
    return text;
  }
  if (!Array.isArray(referencesArray) || referencesArray.length === 0) {
    // If no references, strip markers or leave them? For now, leave them.
    // Consider if markers should be stripped if no corresponding reference exists.
    return text;
  }

  // Create a mapping from original marker number (1, 2, ...) to the new reference ID (e.g., "[1]", "[2]")
  // This assumes the AI initially generates markers like [1], [2] that correspond to the order of its *original* sources list.
  // The `referencesArray` is the *final, ordered* list of references, each with its correct `id` (e.g. "[1]", "[2]").
  
  // The goal is to replace any found marker like `[N]` with `referencesArray[N-1].id`.
  // This makes the function robust even if the AI's original numbering was inconsistent,
  // as long as the `referencesArray` is the single source of truth for order and IDs.

  let renumberedText = text.replace(/\[(\d+)\]/g, (match, originalNumberStr) => {
    const originalNumber = parseInt(originalNumberStr, 10);
    const referenceIndex = originalNumber - 1; // 0-based index

    if (referenceIndex >= 0 && referenceIndex < referencesArray.length) {
      const targetReference = referencesArray[referenceIndex];
      if (targetReference && targetReference.id) {
        // console.log(`renumberInTextCitations: Replacing ${match} with ${targetReference.id}`);
        return targetReference.id;
      }
    }
    // If marker is out of bounds or target reference has no ID, keep original marker.
    // console.warn(`renumberInTextCitations: Marker ${match} out of bounds or target reference missing ID. Kept original.`);
    return match;
  });

  return renumberedText;
};
